import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftRight,
  Handshake,
  PackageX,
  Presentation,
  Replace,
  ShieldCheck,
  Truck,
  Undo2,
} from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import { InventoryActionToast } from "../components/InventoryActionToast";
import {
  AdvanceReplacementToCustomerModal,
  DefectiveReplacementToVendorModal,
  ExcessReturnToVendorModal,
  MaterialDemoToCustomerModal,
  MaterialOnLoanToCustomerModal,
  MaterialOnLoanToVendorModal,
  WarehouseToWarehouseTransferModal,
  WarrantyReplacementToVendorModal,
} from "../components/stock-transfer-modals";

/** API `transferType` values sent to POST /saas/ims/stock-transfer. */
const STOCK_TRANSFER_OPTIONS = [
  {
    id: "warehouse-to-warehouse",
    labelKey: "inventory.transferWarehouseToWarehouse",
    icon: ArrowLeftRight,
    Modal: WarehouseToWarehouseTransferModal,
  },
  {
    id: "defective-replacement-vendor",
    labelKey: "inventory.transferDefectiveReplacementToVendor",
    icon: PackageX,
    Modal: DefectiveReplacementToVendorModal,
  },
  {
    id: "warranty-replacement-vendor",
    labelKey: "inventory.transferWarrantyReplacementToVendor",
    icon: ShieldCheck,
    Modal: WarrantyReplacementToVendorModal,
  },
  {
    id: "material-demo-customer",
    labelKey: "inventory.transferMaterialDemoToCustomer",
    icon: Presentation,
    Modal: MaterialDemoToCustomerModal,
  },
  {
    id: "advance-replacement-customer",
    labelKey: "inventory.transferAdvanceReplacementToCustomer",
    icon: Replace,
    Modal: AdvanceReplacementToCustomerModal,
  },
  {
    id: "material-loan-customer",
    labelKey: "inventory.transferMaterialOnLoanToCustomer",
    icon: Handshake,
    Modal: MaterialOnLoanToCustomerModal,
  },
  {
    id: "material-loan-vendor",
    labelKey: "inventory.transferMaterialOnLoanToVendor",
    icon: Truck,
    Modal: MaterialOnLoanToVendorModal,
  },
  {
    id: "excess-return-vendor",
    labelKey: "inventory.transferExcessReturnToVendor",
    icon: Undo2,
    Modal: ExcessReturnToVendorModal,
  },
];

export function StockTransferPage() {
  const { t } = useTranslation();
  const [openModalId, setOpenModalId] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleSuccess = () => {
    setOpenModalId(null);
    setToast({ message: t("inventory.stockTransferredSuccess"), type: "success" });
  };

  const handleModalOpenChange = (modalId, nextOpen) => {
    if (!nextOpen) setOpenModalId(null);
    else setOpenModalId(modalId);
  };

  return (
    <AppLayout
      hideBreadcrumb
      overlay={<InventoryActionToast toast={toast} />}
      className="space-y-6"
    >
      <PageTitleRow
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
      >
        <h1 className={cn(ui.pageTitle, "uppercase")}>
          {t("inventory.stockTransferTitle")}
        </h1>
      </PageTitleRow>

      <p className="text-sm font-semibold per-file-hint -mt-2">
        {t("inventory.stockTransferSubtitle")}
      </p>

      <ContentCard bodyClassName="p-5 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {STOCK_TRANSFER_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setOpenModalId(option.id)}
                className="flex items-center gap-3 rounded-2xl border border-(--color-text-border) bg-white px-4 py-4 text-left transition hover:border-primary hover:bg-workspace/40 cursor-pointer"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-sm font-bold text-foreground leading-snug">
                  {t(option.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </ContentCard>

      {STOCK_TRANSFER_OPTIONS.map((option) => {
        if (openModalId !== option.id) return null;
        const Modal = option.Modal;
        return (
          <Modal
            key={option.id}
            open
            onOpenChange={(nextOpen) => handleModalOpenChange(option.id, nextOpen)}
            onSuccess={handleSuccess}
          />
        );
      })}
    </AppLayout>
  );
}
